// =============================================================================
// app/src/lib/notas.ts — a régua do MURAL (migração 072).
//
// O que é decisão de LEITURA da grade mora aqui, puro e testado: a paleta, a
// cor de cada pessoa, a ordem das notas e como se monta a coluna de cada um.
// A tela (paginas/Notas.tsx) desenha; ela não decide.
//
// O que NÃO está aqui: quem pode finalizar. Isso é autorização, quem responde
// é o banco, e a resposta chega em `posso_finalizar` de cada linha — a tela
// desenha o botão a partir dela. Recalcular a regra aqui daria duas fontes
// para a mesma verdade, e um dia elas discordariam na cara do vendedor.
// =============================================================================
import type { ColaboradorDoMural, CorDaEquipe, NotaDoMural } from '@core/types';
import { hojeISO, horaBR } from './formato';

/**
 * A PALETA. O banco guarda o nome da cor (migração 072); o tom que se lê no
 * fundo #111 é decisão de tela e mora aqui — é a razão de o hex não estar lá.
 *
 * Os oito foram escolhidos para se distinguirem A DOIS METROS, que é a
 * distância de quem passa pelo balcão e olha o monitor de lado. Por isso não
 * há dois azuis nem dois verdes: com cinco pessoas, sobra paleta.
 *
 * Vermelho ficou de fora (regra 10 do CLAUDE.md): #D90429 é a marca e, nesta
 * tela, o sinal de URGENTE. Uma pessoa vermelha faria a coluna dela parecer
 * urgente o dia todo, e o urgente de verdade deixaria de saltar.
 */
export const CORES_DA_EQUIPE = {
  azul:    { rotulo: 'Azul',    hex: '#5B9DFF' },
  ciano:   { rotulo: 'Ciano',   hex: '#22D3EE' },
  verde:   { rotulo: 'Verde',   hex: '#35C46A' },
  lima:    { rotulo: 'Lima',    hex: '#A3E635' },
  amarelo: { rotulo: 'Amarelo', hex: '#FFC400' },
  laranja: { rotulo: 'Laranja', hex: '#FB923C' },
  rosa:    { rotulo: 'Rosa',    hex: '#F472B6' },
  roxo:    { rotulo: 'Roxo',    hex: '#A78BFA' },
} as const satisfies Record<CorDaEquipe, { rotulo: string; hex: string }>;

export const CORES_DISPONIVEIS = Object.keys(CORES_DA_EQUIPE) as CorDaEquipe[];

/** O teto do texto — o mesmo da RPC e do CHECK da tabela. Repetido aqui só
 *  para o contador da tela avisar ANTES de o banco recusar. */
export const LIMITE_TEXTO = 500;

/**
 * A cor de quem ainda não tem cor escolhida.
 *
 * Podia ser cinza, e cinza seria honesto ("ninguém definiu"). Seria também uma
 * grade meio apagada até o dono sentar e pintar cinco pessoas — e enquanto
 * isso a tela não cumpre o que promete, que é reconhecer a coluna pela cor.
 * Então derivamos do id: é ESTÁVEL (a mesma pessoa tem sempre o mesmo tom, que
 * é a única propriedade que importa) e some no instante em que o dono escolhe.
 *
 * Duas pessoas podem cair na mesma cor provisória. Não é bug e não vale
 * corrigir com sorteio: a tela Equipe mostra quem já usa cada cor, e o
 * conserto é escolher — que é o que se quer que aconteça de qualquer forma.
 */
export function corProvisoria(id: string): CorDaEquipe {
  let soma = 0;
  for (let i = 0; i < id.length; i += 1) {
    // 31 e o módulo primo: espalhamento barato e determinístico. Não é hash
    // criptográfico e não precisa ser — o pior caso é dois roxos.
    soma = (soma * 31 + id.charCodeAt(i)) % 1_000_000_007;
  }
  // O `??` é para o compilador (noUncheckedIndexedAccess), não para a lógica:
  // o módulo do tamanho nunca sai do array. Um `!` calaria o tipo do mesmo
  // jeito e sem deixar a tela em pé se um dia a paleta ficar vazia.
  return CORES_DISPONIVEIS[soma % CORES_DISPONIVEIS.length] ?? 'azul';
}

/** A cor desta pessoa: a escolhida, ou a provisória enquanto não há escolha. */
export function corDe(pessoa: { id: string; cor: CorDaEquipe | null }): CorDaEquipe {
  return pessoa.cor ?? corProvisoria(pessoa.id);
}

/** O hex que a tela pinta. */
export function hexDe(pessoa: { id: string; cor: CorDaEquipe | null }): string {
  return CORES_DA_EQUIPE[corDe(pessoa)].hex;
}

/**
 * A ordem dentro de uma coluna: URGENTE primeiro, depois a mais recente.
 *
 * O banco já devolve assim, e repetir aqui não é desconfiança: a tela reordena
 * sozinha quando alguém marca urgente sem recarregar, e uma lista que muda de
 * critério entre o servidor e o clique é uma lista que pula na cara de quem
 * está lendo.
 */
export function ordenarNotas(notas: NotaDoMural[]): NotaDoMural[] {
  return [...notas].sort((a, b) => {
    if (a.urgente !== b.urgente) return a.urgente ? -1 : 1;
    return b.criada_em.localeCompare(a.criada_em);
  });
}

/** Uma coluna da grade: a pessoa, a cor dela e o que está pendurado. */
export interface ColunaDoMural {
  id: string;
  nome: string;
  cor: CorDaEquipe;
  hex: string;
  ativo: boolean;
  notas: NotaDoMural[];
}

/**
 * A GRADE. Uma coluna por colaborador, na ordem que o banco mandou (ativos
 * primeiro, depois alfabética) — a coluna do Renan tem que estar no mesmo
 * lugar amanhã, senão a cor perde a serventia.
 *
 * Coluna VAZIA continua na grade: ela é a equipe, não a lista de quem falou
 * hoje. E a nota de um autor que não veio na lista de colunas ganha uma coluna
 * própria no fim, montada com o que a própria nota carrega — é defesa contra
 * as duas consultas chegarem de momentos diferentes (alguém pendurou a
 * primeira nota entre uma e outra). Sem isso, a nota simplesmente sumiria da
 * tela, que é a única falha inaceitável num mural.
 */
export function montarGrade(
  colaboradores: ColaboradorDoMural[],
  notas: NotaDoMural[],
): ColunaDoMural[] {
  const porAutor = new Map<string, NotaDoMural[]>();
  for (const nota of notas) {
    const lista = porAutor.get(nota.autor_id);
    if (lista) lista.push(nota);
    else porAutor.set(nota.autor_id, [nota]);
  }

  const colunas: ColunaDoMural[] = colaboradores.map((c) => {
    const doAutor = porAutor.get(c.id) ?? [];
    porAutor.delete(c.id);
    return {
      id: c.id,
      nome: c.nome,
      cor: corDe(c),
      hex: hexDe(c),
      ativo: c.ativo,
      notas: ordenarNotas(doAutor),
    };
  });

  // O que sobrou no mapa são autores sem coluna — ver o comentário acima.
  for (const [autorId, doAutor] of porAutor) {
    const primeira = doAutor[0];
    if (!primeira) continue;   // lista vazia não chega a entrar no mapa
    const pessoa = { id: autorId, cor: primeira.autor_cor };
    colunas.push({
      id: autorId,
      nome: primeira.autor_nome,
      cor: corDe(pessoa),
      hex: hexDe(pessoa),
      ativo: primeira.autor_ativo,
      notas: ordenarNotas(doAutor),
    });
  }

  return colunas;
}

/** O contador do topo (e do atalho na barra): quantas e quantas gritam. */
export function resumoDoMural(notas: NotaDoMural[]): {
  penduradas: number;
  urgentes: number;
} {
  return {
    penduradas: notas.length,
    urgentes: notas.filter((n) => n.urgente).length,
  };
}

/**
 * QUANDO, em pt-BR de balcão. A nota é lida de relance: "há 20 min" diz o que
 * "01/09/2026 14:32" faria a pessoa calcular.
 *
 * `agora` entra por parâmetro para o texto ser testável — relógio dentro de
 * função pura é teste que quebra sozinho de madrugada.
 */
export function quandoCurto(iso: string, agora: Date): string {
  const minutos = Math.floor((agora.getTime() - new Date(iso).getTime()) / 60_000);

  // Relógio do aparelho atrasado em relação ao servidor: nota "do futuro". Não
  // vale mostrar "há -2 min" — para quem lê, ela acabou de chegar.
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const dia = hojeISO(new Date(iso));
  const hoje = hojeISO(agora);
  if (dia === hoje) return horaBR(iso);

  const ontem = hojeISO(new Date(agora.getTime() - 86_400_000));
  if (dia === ontem) return `ontem ${horaBR(iso)}`;

  return dia.slice(5).split('-').reverse().join('/');
}
